import React, { useState, useEffect, useRef } from "react";

export default function ContactModal({ isOpen, onClose }) {
  const [input, setInput] = useState({
    fname: "",
    lname: "",
    email: "",
    mobileno: "",
    msg: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const modalRef = useRef(null);

  // Close on Escape key and lock body scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Reset errors/status when modal closes
  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setStatusMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const handleInputs = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (statusMessage) {
      setStatusMessage(null);
    }
  };

  const validate = () => {
    const newErrors = {};
    const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const MOBILE_REGEX = /^[0-9+\s-]{7,15}$/;

    if (!input.fname.trim()) {
      newErrors.fname = "First name is required";
    }
    if (!input.lname.trim()) {
      newErrors.lname = "Last name is required";
    }
    if (!input.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!EMAIL_REGEX.test(input.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!input.mobileno.trim()) {
      newErrors.mobileno = "Phone number is required";
    } else if (!MOBILE_REGEX.test(input.mobileno.trim())) {
      newErrors.mobileno = "Please enter a valid phone number (7-15 digits)";
    }
    if (!input.msg.trim()) {
      newErrors.msg = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsLoading(true);
    setStatusMessage(null);

    try {
      const payload = {
        fname: input.fname.trim(),
        lname: input.lname.trim(),
        email: input.email.trim(),
        mobileno: input.mobileno.trim(),
        msg: input.msg.trim(),
      };

      const response = await fetch(
        "https://back234genxmeta.com/api/auth/Q09OVEFDVERFVEFJTFM=",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => null);

      if (response.ok) {
        setStatusMessage({
          type: "success",
          text:
            data?.message ||
            "Thank you! Your inquiry has been sent successfully. Our team will contact you shortly.",
        });
        setInput({
          fname: "",
          lname: "",
          email: "",
          mobileno: "",
          msg: "",
        });
        setErrors({});
      } else {
        setStatusMessage({
          type: "error",
          text:
            data?.message ||
            data?.error ||
            `Failed to submit form (Status: ${response.status}). Please try again.`,
        });
      }
    } catch (error) {
      console.error("API error:", error);
      setStatusMessage({
        type: "error",
        text: "Unable to connect to the server. Please check your connection or try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="contact-modal-overlay"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contactModalTitle"
    >
      <div className="contact-modal-dialog" ref={modalRef}>
        {/* Header */}
        <div className="contact-modal-header">
          <div>
            <div className="contact-modal-badge">
              <span style={{ fontSize: "11px" }}>✨</span> Get in Touch
            </div>
            <h3 id="contactModalTitle" className="contact-modal-title">
              Let's Initiate Your Project
            </h3>
            <p className="contact-modal-subtitle">
              Fill out the form below and our tech experts will reach out to you within 24 hours.
            </p>
          </div>
          <button
            type="button"
            className="contact-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="contact-modal-body">
          {/* Quick contact pills */}
          <div className="contact-modal-quick-info">
            <a href="tel:+918860132221" className="contact-modal-quick-item">
              <span className="badge rounded-pill px-2 py-0" style={{ background: "rgba(0, 210, 255, 0.15)", color: "#00d2ff", border: "1px solid rgba(0, 210, 255, 0.3)", fontSize: "10px" }}>India</span>
              <span>+91 88601 32221</span>
            </a>
            <span className="contact-modal-quick-divider">•</span>
            <a href="tel:+971501855369" className="contact-modal-quick-item">
              <span className="badge rounded-pill px-2 py-0" style={{ background: "rgba(0, 210, 255, 0.15)", color: "#00d2ff", border: "1px solid rgba(0, 210, 255, 0.3)", fontSize: "10px" }}>Dubai</span>
              <span>+971 50 185 5369</span>
            </a>
            <span className="contact-modal-quick-divider">•</span>
            <a href="mailto:us@genxmeta.com" className="contact-modal-quick-item">
              <span style={{ color: "#00d2ff" }}>✉</span>
              <span>us@genxmeta.com</span>
            </a>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="contact-modal-form row g-2" noValidate>
            {statusMessage && (
              <div className="col-12 mb-2">
                <div
                  className={`alert ${
                    statusMessage.type === "success"
                      ? "alert-success bg-success bg-opacity-25 text-white border-success"
                      : "alert-danger bg-danger bg-opacity-25 text-white border-danger"
                  } d-flex align-items-center justify-content-between py-2 px-3 rounded`}
                  role="alert"
                >
                  <div className="font_14">{statusMessage.text}</div>
                  <button
                    type="button"
                    className="btn-close btn-close-white ms-2"
                    aria-label="Close alert"
                    onClick={() => setStatusMessage(null)}
                  ></button>
                </div>
              </div>
            )}

            <div className="col-md-6 col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.fname ? "is-invalid" : ""}`}
                  id="modalFirstName"
                  placeholder="First Name"
                  name="fname"
                  value={input.fname}
                  onChange={handleInputs}
                  disabled={isLoading}
                />
                <label htmlFor="modalFirstName">First Name</label>
                {errors.fname && (
                  <div className="text-danger font_12 mt-1 ps-1">{errors.fname}</div>
                )}
              </div>
            </div>

            <div className="col-md-6 col-12">
              <div className="form-floating">
                <input
                  type="text"
                  className={`form-control ${errors.lname ? "is-invalid" : ""}`}
                  id="modalLastName"
                  placeholder="Last Name"
                  name="lname"
                  value={input.lname}
                  onChange={handleInputs}
                  disabled={isLoading}
                />
                <label htmlFor="modalLastName">Last Name</label>
                {errors.lname && (
                  <div className="text-danger font_12 mt-1 ps-1">{errors.lname}</div>
                )}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="email"
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  id="modalEmail"
                  placeholder="Email Address"
                  name="email"
                  value={input.email}
                  onChange={handleInputs}
                  disabled={isLoading}
                />
                <label htmlFor="modalEmail">Email Address</label>
                {errors.email && (
                  <div className="text-danger font_12 mt-1 ps-1">{errors.email}</div>
                )}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <input
                  type="tel"
                  className={`form-control ${errors.mobileno ? "is-invalid" : ""}`}
                  id="modalPhone"
                  placeholder="Phone Number"
                  name="mobileno"
                  value={input.mobileno}
                  onChange={handleInputs}
                  disabled={isLoading}
                />
                <label htmlFor="modalPhone">Phone Number</label>
                {errors.mobileno && (
                  <div className="text-danger font_12 mt-1 ps-1">{errors.mobileno}</div>
                )}
              </div>
            </div>

            <div className="col-12">
              <div className="form-floating">
                <textarea
                  className={`form-control ${errors.msg ? "is-invalid" : ""}`}
                  placeholder="Message"
                  id="modalMessage"
                  name="msg"
                  style={{ height: "100px" }}
                  value={input.msg}
                  onChange={handleInputs}
                  disabled={isLoading}
                />
                <label htmlFor="modalMessage">Your Message / Requirements</label>
                {errors.msg && (
                  <div className="text-danger font_12 mt-1 ps-1">{errors.msg}</div>
                )}
              </div>
            </div>

            <div className="col-12 mt-2">
              <button
                type="submit"
                className="btn contact-modal-submit-btn w-100 d-flex align-items-center justify-content-center gap-2"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Sending Message...
                  </>
                ) : (
                  <>
                    <span>Submit Inquiry</span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  </>
                )}
              </button>
            </div>

            <div className="col-12 text-center mt-2">
              <p className="font_12 text-secondary mb-0">
                🔒 We respect your privacy. No spam ever.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
